import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { NewspaperIcon, CheckCircleIcon, DocumentTextIcon, ChevronDownIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

const pressSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Phone number must be at least 10 characters'),
  mediaOutlet: z.string().min(2, 'Media outlet name is required'),
  position: z.string().min(2, 'Position is required'),
  mediaType: z.array(z.string()).min(1, 'At least one media type is required'),
  otherMediaType: z.string().optional(),
  teamSize: z.string().min(1, 'Team size is required'),
  equipmentDetails: z.string().optional(),
  coveragePlans: z.string().min(10, 'Please provide details about your coverage plans'),
  arrivalDate: z.string().min(1, 'Arrival date is required'),
  specialRequests: z.string().optional(),
  tournamentId: z.string().optional(),
});

type FormData = z.infer<typeof pressSchema>;

export default function MobilePress() {
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [regNumber, setRegNumber] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [guidelinesOpen, setGuidelinesOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tournaments, setTournaments] = useState<{ id: string; name: string }[]>([]);
  const [teamMembers, setTeamMembers] = useState<{ firstName: string; lastName: string }[]>([{ firstName: '', lastName: '' }]);

  const { register, handleSubmit, formState: { errors }, watch, reset, setValue } = useForm<FormData>({
    resolver: zodResolver(pressSchema),
  });
  const selectedMediaTypes = watch('mediaType') as string[] | undefined;
  const selectedTeamSize = watch('teamSize');
  const isMultiPerson = selectedTeamSize && selectedTeamSize !== '1';

  const updateTeamMember = (index: number, field: 'firstName' | 'lastName', value: string) => {
    setTeamMembers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };
  const addTeamMember = () => setTeamMembers(prev => [...prev, { firstName: '', lastName: '' }]);
  const removeTeamMember = (index: number) => setTeamMembers(prev => prev.filter((_, i) => i !== index));

  const toggleMediaType = (value: string) => {
    const current = selectedMediaTypes ?? [];
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    setValue('mediaType', updated, { shouldValidate: true });
  };

  useEffect(() => {
    Promise.all([fetchGuidelines(), fetchTournaments()]).finally(() => setLoading(false));
  }, []);

  const fetchGuidelines = async () => {
    try {
      const { data } = await supabase
        .from('content_items')
        .select('content')
        .eq('title', 'Media Guidelines')
        .eq('status', 'Published')
        .maybeSingle();
      if (data?.content) {
        let content = data.content;
        try { if (typeof content === 'string') content = JSON.parse(content); } catch {}
        setGuidelines(content);
      }
    } catch {}
  };

  const fetchTournaments = async () => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('id, name')
        .eq('status', 'upcoming')
        .order('start_date', { ascending: true });
      if (data?.length) {
        setTournaments(data);
        setValue('tournamentId', data[0].id);
      }
    } catch {}
  };

  const onSubmit = async (data: FormData) => {
    try {
      setSubmitting(true);
      const rn = `PRESS-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
      setRegNumber(rn);

      const common = {
        email: data.email,
        phone: data.phone,
        media_outlet: data.mediaOutlet,
        position: data.position,
        media_type: data.mediaType,
        other_media_type: data.mediaType.includes('Other') ? data.otherMediaType : null,
        team_size: data.teamSize,
        equipment_details: data.equipmentDetails || null,
        coverage_plans: data.coveragePlans,
        arrival_date: data.arrivalDate,
        special_requests: data.specialRequests || null,
        registration_number: rn,
        status: 'pending',
        tournament_id: data.tournamentId || null,
      };

      const validMembers = isMultiPerson ? teamMembers.filter(m => m.firstName.trim() || m.lastName.trim()) : [];
      const rows = [
        { ...common, first_name: data.firstName, last_name: data.lastName, is_primary: true },
        ...validMembers.map(m => ({
          ...common,
          first_name: m.firstName.trim(),
          last_name: m.lastName.trim(),
          is_primary: false,
        })),
      ];

      const { error } = await supabase.from('press_registrations').insert(rows);

      if (error) throw error;
      setConfirmed(true);
      setTeamMembers([{ firstName: '', lastName: '' }]);
      reset();
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full text-sm border border-gray-200 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-golf-orange bg-white text-gray-700 placeholder-gray-400';
  const labelClass = 'block text-xs font-semibold text-gray-600 mb-1';
  const errorClass = 'text-xs text-red-500 mt-1';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="pb-10">
      {/* Header */}
      <div
        className="relative h-28 bg-cover bg-center overflow-hidden"
        style={{ backgroundImage: `url(https://images.pexels.com/photos/1325681/pexels-photo-1325681.jpeg?auto=compress&w=800)` }}
      >
        <div className="absolute inset-0 bg-golf-gray/80" />
        <div className="relative z-10 h-full flex items-end p-4">
          <div>
            <p className="text-golf-orange text-xs font-semibold uppercase tracking-wider mb-1">Media Accreditation</p>
            <h1 className="text-white font-display font-bold text-xl">Press Registration</h1>
          </div>
        </div>
      </div>

      <div className="px-4 pt-4">
        {confirmed ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 text-center">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="font-display font-bold text-golf-gray text-lg mb-2">Registration Successful</h2>
            <p className="text-sm text-gray-500 mb-3 leading-relaxed">
              Your press registration has been received. We'll contact you with further instructions.
            </p>
            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Registration Number</p>
              <p className="font-bold text-golf-gray text-sm">{regNumber}</p>
            </div>
            <button
              onClick={() => setConfirmed(false)}
              className="bg-golf-orange text-white text-sm font-semibold px-6 py-2.5 rounded-xl w-full active:opacity-80"
            >
              Register Another
            </button>
          </div>
        ) : (
          <>
            {/* Guidelines Collapsible */}
            {guidelines && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden mb-4">
                <button
                  onClick={() => setGuidelinesOpen(v => !v)}
                  className="w-full flex items-center justify-between p-4 text-left"
                >
                  <div className="flex items-center gap-2">
                    <DocumentTextIcon className="w-4 h-4 text-golf-orange" />
                    <span className="font-semibold text-golf-gray text-sm">Media Guidelines</span>
                  </div>
                  <ChevronDownIcon className={`w-4 h-4 text-gray-400 transition-transform ${guidelinesOpen ? 'rotate-180' : ''}`} />
                </button>
                {guidelinesOpen && (
                  <div className="px-4 pb-4 border-t border-gray-50">
                    <div
                      className="prose prose-sm max-w-none text-gray-600 mt-3"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(guidelines) as string) }}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Form */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-center gap-2 mb-4">
                <NewspaperIcon className="w-5 h-5 text-golf-orange" />
                <h2 className="font-display font-bold text-golf-gray text-base">Media Registration Form</h2>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Personal Info */}
                <div>
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Personal Information</p>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className={labelClass}>First Name <span className="text-red-400">*</span></label>
                      <input type="text" {...register('firstName')} className={inputClass} placeholder="John" />
                      {errors.firstName && <p className={errorClass}>{errors.firstName.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Last Name <span className="text-red-400">*</span></label>
                      <input type="text" {...register('lastName')} className={inputClass} placeholder="Doe" />
                      {errors.lastName && <p className={errorClass}>{errors.lastName.message}</p>}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className={labelClass}>Email Address <span className="text-red-400">*</span></label>
                      <input type="email" {...register('email')} className={inputClass} placeholder="john@media.com" />
                      {errors.email && <p className={errorClass}>{errors.email.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Phone Number <span className="text-red-400">*</span></label>
                      <input type="tel" {...register('phone')} className={inputClass} placeholder="+234 800 000 0000" />
                      {errors.phone && <p className={errorClass}>{errors.phone.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Media Info */}
                <div className="pt-4 border-t border-gray-50">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Media Information</p>
                  <div className="space-y-3">
                    <div>
                      <label className={labelClass}>Media Outlet <span className="text-red-400">*</span></label>
                      <input type="text" {...register('mediaOutlet')} className={inputClass} placeholder="e.g., Golf Nigeria" />
                      {errors.mediaOutlet && <p className={errorClass}>{errors.mediaOutlet.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Position <span className="text-red-400">*</span></label>
                      <input type="text" {...register('position')} className={inputClass} placeholder="e.g., Journalist" />
                      {errors.position && <p className={errorClass}>{errors.position.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Media Type <span className="text-red-400">*</span></label>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {['Print', 'Digital', 'TV', 'Radio', 'Photography', 'Other'].map(type => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => toggleMediaType(type)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                              (selectedMediaTypes ?? []).includes(type)
                                ? 'bg-golf-orange text-white border-golf-orange'
                                : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                      {errors.mediaType && <p className={errorClass}>{errors.mediaType.message}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Team Size <span className="text-red-400">*</span></label>
                      <select {...register('teamSize')} className={inputClass}>
                        <option value="">Select size</option>
                        <option value="1">Just me (1)</option>
                        <option value="2-3">Small (2-3)</option>
                        <option value="4-6">Medium (4-6)</option>
                        <option value="7+">Large (7+)</option>
                      </select>
                      {errors.teamSize && <p className={errorClass}>{errors.teamSize.message}</p>}
                    </div>
                    {isMultiPerson && (
                      <div>
                        <label className={labelClass}>Team Member Names</label>
                        <div className="space-y-3">
                          {teamMembers.map((member, index) => (
                            <div key={index} className="space-y-1">
                              <div className="flex items-center gap-1 mb-1">
                                <span className="text-xs text-gray-400 shrink-0">{index + 1}.</span>
                                {teamMembers.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeTeamMember(index)}
                                    className="ml-auto shrink-0 text-gray-400 hover:text-red-500 transition-colors"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={member.firstName}
                                  onChange={e => updateTeamMember(index, 'firstName', e.target.value)}
                                  placeholder="First name"
                                  className={inputClass}
                                />
                                <input
                                  type="text"
                                  value={member.lastName}
                                  onChange={e => updateTeamMember(index, 'lastName', e.target.value)}
                                  placeholder="Last name"
                                  className={inputClass}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={addTeamMember}
                          className="mt-2 flex items-center gap-1 text-xs text-golf-orange hover:text-golf-orange/80 transition-colors"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                          Add another member
                        </button>
                      </div>
                    )}
                    {(selectedMediaTypes ?? []).includes('Other') && (
                      <div>
                        <label className={labelClass}>Please Specify <span className="text-red-400">*</span></label>
                        <input type="text" {...register('otherMediaType')} className={inputClass} />
                        {errors.otherMediaType && <p className={errorClass}>{errors.otherMediaType.message}</p>}
                      </div>
                    )}
                    <div>
                      <label className={labelClass}>Equipment Details</label>
                      <textarea
                        {...register('equipmentDetails')}
                        rows={2}
                        className={inputClass}
                        placeholder="Cameras, tripods, recording devices..."
                      />
                    </div>
                  </div>
                </div>

                {/* Coverage Details */}
                <div className="pt-4 border-t border-gray-50">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Coverage Details</p>
                  <div className="space-y-3">
                    <div>
                      <label className={labelClass}>Coverage Plans <span className="text-red-400">*</span></label>
                      <textarea
                        {...register('coveragePlans')}
                        rows={3}
                        className={inputClass}
                        placeholder="Describe your coverage plans..."
                      />
                      {errors.coveragePlans && <p className={errorClass}>{errors.coveragePlans.message}</p>}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelClass}>Arrival Date <span className="text-red-400">*</span></label>
                        <input type="date" {...register('arrivalDate')} className={inputClass} />
                        {errors.arrivalDate && <p className={errorClass}>{errors.arrivalDate.message}</p>}
                      </div>
                      {tournaments.length > 0 && (
                        <div>
                          <label className={labelClass}>Tournament</label>
                          <select {...register('tournamentId')} className={inputClass}>
                            {tournaments.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className={labelClass}>Special Requests</label>
                      <textarea
                        {...register('specialRequests')}
                        rows={2}
                        className={inputClass}
                        placeholder="Any special requirements..."
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-golf-orange text-white font-semibold text-sm py-3 rounded-xl active:opacity-80 disabled:opacity-50 transition-opacity"
                >
                  {submitting ? 'Submitting...' : 'Submit Registration'}
                </button>
                <p className="text-[11px] text-gray-400 text-center leading-relaxed">
                  By submitting, you agree to abide by the media guidelines and code of conduct for the Ibom Open.
                </p>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
